// =============================================================================
// Tiny synthesized UI sounds via the Web Audio API — no audio assets needed.
//
// The check-off sound is deliberately soft and organic ("gooey"): a sine
// that glides DOWN in pitch (liquids sound like falling pitch), a little
// bubble-chirp at the tail, and a low-passed noise "squish" at the attack.
// Nothing percussive — no sharp transients, so it can't read as a drum hit.
//
// AudioContext must be created/resumed inside a user gesture (iOS); all
// callers are click handlers, so getCtx() is safe there.
// =============================================================================

let ctx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    if (!ctx) ctx = new AC();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
};

// Short buffer of low-passed noise for the squishy attack.
const noiseBurst = (ac, t, duration, gainValue) => {
  const len = Math.max(1, Math.floor(ac.sampleRate * duration));
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 420;
  const g = ac.createGain();
  g.gain.setValueAtTime(gainValue, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);
  src.connect(lp).connect(g).connect(ac.destination);
  src.start(t);
  src.stop(t + duration);
};

// Gooey "blub" — task checked off.
export const playBlub = () => {
  const ac = getCtx();
  if (!ac) return;
  const t = ac.currentTime;

  // squishy attack
  noiseBurst(ac, t, 0.06, 0.08);

  // main falling bloop
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(340, t);
  osc.frequency.exponentialRampToValueAtTime(95, t + 0.16);
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 750;
  lp.Q.value = 4;
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.22, t + 0.015);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
  osc.connect(lp).connect(g).connect(ac.destination);
  osc.start(t);
  osc.stop(t + 0.24);

  // little foamy bubble at the tail
  const osc2 = ac.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(150, t + 0.1);
  osc2.frequency.exponentialRampToValueAtTime(300, t + 0.18);
  const g2 = ac.createGain();
  g2.gain.setValueAtTime(0.0001, t + 0.1);
  g2.gain.exponentialRampToValueAtTime(0.07, t + 0.12);
  g2.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
  osc2.connect(g2).connect(ac.destination);
  osc2.start(t + 0.1);
  osc2.stop(t + 0.22);
};

const sounds = { playBlub };
export default sounds;
